/**
 * RuleApplies (YAG-359) — the "applies to…" affordance on a Playbook rule:
 * a chip when scoped, an inline editor to set/change/clear the scope.
 * Unscoped rules render exactly as before (the chip simply absent) — the
 * affordance is the small "applies to…" action.
 *
 * Saving goes through PUT /rules/:id/scope, which supersedes server-side —
 * the rule comes back with a NEW id; the parent swaps its row.
 *
 * Workflow and role are native selects (YAG-463): a rule scoped to a role
 * that resolves to nothing is silently dropped from routing server-side, so
 * free text here was a quiet way to write a rule nobody would ever run. The
 * select lists the Team's live role slugs; a rule already scoped to a
 * retired slug keeps that slug as an extra option so old rules stay
 * editable without losing their value.
 */
import { useEffect, useState } from "react";

export interface RuleScope {
  workflow?: string;
  role?: string;
  repo?: string;
  paths?: string[];
}

export function scopeChipText(scope: RuleScope): string {
  const parts = [
    scope.workflow,
    scope.role,
    scope.repo,
    scope.paths && scope.paths.length > 0 ? scope.paths.join(", ") : null,
  ].filter(Boolean);
  return parts.join(" · ");
}

export interface RuleAppliesProps {
  deskId: string;
  ruleId: string;
  scope: RuleScope | null;
  /** The supersede returns a fresh row; the parent swaps id + scope. */
  onSaved: (input: { oldRuleId: string; newRuleId: string; scope: RuleScope | null }) => void;
}

export function RuleApplies({ deskId, ruleId, scope, onSaved }: RuleAppliesProps) {
  const [editing, setEditing] = useState(false);
  const [workflow, setWorkflow] = useState(scope?.workflow ?? "");
  /* `code_review` is the only cataloged workflow today, but the scope shape is
     generic and future workflows ship by adding catalog entries — "Other…"
     keeps arbitrary workflow values writable instead of hardcoding the list. */
  const [workflowCustom, setWorkflowCustom] = useState(false);
  const [role, setRole] = useState(scope?.role ?? "");
  const [repo, setRepo] = useState(scope?.repo ?? "");
  const [paths, setPaths] = useState(scope?.paths?.join(", ") ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [roleSlugs, setRoleSlugs] = useState<string[]>([]);

  useEffect(() => {
    if (!editing) return;
    let cancelled = false;
    void (async () => {
      try {
        // The datalist follows the selected workflow; "Any" falls back to the
        // review roster (the commonest scope target).
        const rosterWorkflow = workflow.trim() || "code_review";
        const res = await fetch(
          `/api/desks/${encodeURIComponent(deskId)}/playbook-roles?workflow=${encodeURIComponent(rosterWorkflow)}`,
          { credentials: "include" },
        );
        if (!res.ok) return;
        const body = (await res.json()) as { roles?: { slug: string }[] };
        if (!cancelled) setRoleSlugs((body.roles ?? []).map((r) => r.slug));
      } catch {
        // No datalist, but the field still takes free text.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editing, deskId, workflow]);

  const save = async (next: RuleScope | null) => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/desks/${encodeURIComponent(deskId)}/rules/${encodeURIComponent(ruleId)}/scope`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ scope: next }),
        },
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setError(body.error ?? "Saving the scope failed.");
        return;
      }
      const body = (await res.json()) as { rule: { id: string } };
      setEditing(false);
      onSaved({ oldRuleId: ruleId, newRuleId: body.rule.id, scope: next });
    } catch {
      setError("Saving the scope failed.");
    } finally {
      setSaving(false);
    }
  };

  const buildScope = (): RuleScope | null => {
    const next: RuleScope = {};
    if (workflow.trim()) next.workflow = workflow.trim();
    if (role.trim()) next.role = role.trim();
    if (repo.trim()) next.repo = repo.trim();
    const pathList = paths
      .split(/[,\n]/)
      .map((p) => p.trim())
      .filter(Boolean);
    if (pathList.length > 0) next.paths = pathList;
    return Object.keys(next).length > 0 ? next : null;
  };

  if (!editing) {
    return (
      <>
        {scope ? (
          <span className="playbook-rule-scope" data-testid={`rule-scope-${ruleId}`}>
            applies to: {scopeChipText(scope)}
          </span>
        ) : null}
        <button
          type="button"
          className="conn-action"
          onClick={() => setEditing(true)}
          data-testid={`rule-scope-edit-${ruleId}`}
        >
          applies to…
        </button>
      </>
    );
  }

  return (
    <form
      className="playbook-rule-scope-editor"
      data-testid={`rule-scope-editor-${ruleId}`}
      onSubmit={(e) => {
        e.preventDefault();
        void save(buildScope());
      }}
    >
      <label className="playbook-scope-field">
        <span className="playbook-scope-label">Workflow</span>
        {workflowCustom ? (
          <input
            type="text"
            className="playbook-scope-input"
            value={workflow}
            placeholder="workflow name"
            autoFocus
            onChange={(e) => setWorkflow(e.target.value)}
            data-testid={`rule-scope-workflow-${ruleId}`}
          />
        ) : (
          <select
            className="playbook-scope-input"
            value={workflow}
            onChange={(e) => {
              if (e.target.value === "__other") {
                setWorkflowCustom(true);
              } else {
                setWorkflow(e.target.value);
              }
            }}
            data-testid={`rule-scope-workflow-${ruleId}`}
          >
            <option value="">Any workflow</option>
            {workflow !== "" && workflow !== "code_review" && workflow !== "plan_review" ? (
              <option value={workflow}>{workflow}</option>
            ) : null}
            <option value="code_review">code_review</option>
            <option value="plan_review">plan_review</option>
            <option value="__other">Other…</option>
          </select>
        )}
      </label>
      <label className="playbook-scope-field">
        <span className="playbook-scope-label">Role</span>
        <select
          className="playbook-scope-input"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          data-testid={`rule-scope-role-${ruleId}`}
        >
          <option value="">Any role</option>
          {role !== "" && !roleSlugs.includes(role) ? (
            <option value={role}>{role}</option>
          ) : null}
          {roleSlugs.map((slug) => (
            <option key={slug} value={slug}>
              {slug}
            </option>
          ))}
        </select>
      </label>
      <label className="playbook-scope-field">
        <span className="playbook-scope-label">Repo</span>
        <input
          type="text"
          className="playbook-scope-input"
          value={repo}
          placeholder="owner/name"
          onChange={(e) => setRepo(e.target.value)}
        />
      </label>
      <label className="playbook-scope-field">
        <span className="playbook-scope-label">Paths</span>
        <input
          type="text"
          className="playbook-scope-input"
          value={paths}
          placeholder="globs, comma-separated"
          onChange={(e) => setPaths(e.target.value)}
        />
      </label>
      {error ? <p className="playbook-role-error">{error}</p> : null}
      <div className="playbook-scope-actions">
        <button
          type="submit"
          className="playbook-add-btn"
          disabled={saving}
          data-testid={`rule-scope-save-${ruleId}`}
        >
          Save
        </button>
        {scope ? (
          <button
            type="button"
            className="conn-action"
            disabled={saving}
            onClick={() => void save(null)}
            data-testid={`rule-scope-clear-${ruleId}`}
          >
            Clear scope
          </button>
        ) : null}
        <button type="button" className="conn-action" onClick={() => setEditing(false)}>
          Cancel
        </button>
      </div>
    </form>
  );
}
